import type { Age } from '@wodi/domain';
import { Link } from 'react-aria-components';

import styles from './PatientBanner.module.css';

export interface BannerAllergy {
  /** The substance as recorded, e.g. "Penicillin". */
  substance: string;
  /** The recorded reaction, e.g. "anaphylaxis". Shown in brackets when present. */
  reaction?: string;
}

/**
 * Allergies are always stated in one of three explicit forms (DESIGN.md §6.2, CLAUDE.md
 * clinical safety rules). There is no "unknown" or empty variant, so a blank allergy
 * area can't type-check. A `list` with no entries at runtime is shown as "Allergies not
 * recorded", never as blank and never as "No known allergies".
 */
export type BannerAllergies =
  | { state: 'list'; allergies: readonly [BannerAllergy, ...BannerAllergy[]] }
  | { state: 'no_known_allergies' }
  | { state: 'not_recorded' };

export interface PatientBannerProps {
  name: string;
  /** Short display of the recorded sex, from the configured value set, e.g. "F". */
  sex: string;
  /** Age on today's date, from `ageAt` in @wodi/domain. `null` when no date of birth is recorded. */
  age: Age | null;
  /** The date of birth was worked out from an estimated age. */
  ageEstimated: boolean;
  patientNumber: string;
  /** The national Client Registry ID, when the patient has one. */
  crId: string | null;
  /** When there is no CR ID: the registry state in words, e.g. "Waiting to send to the national registry". */
  registryStatus?: string;
  /** Payer and eligibility in words, e.g. "Cash". Set per visit, so absent without one. */
  payer?: string;
  /** The open visit, if there is one. */
  visit?: { number: string; type: string };
  allergies: BannerAllergies;
  /** Where the name links to: the patient page (later, the summary drawer). */
  href?: string;
  /**
   * The patient is not identified yet (casualty): the banner says "Not identified" and
   * shows the temporary label (docs/plans/phase2-casualty.md §1.1 item 4).
   */
  unidentified?: { temporaryLabel: string };
  /** Temporary labels of unknown records merged into this patient's (ADR 0024). */
  alsoRecordedAs?: readonly string[];
  /**
   * The patient is recorded as deceased: a solid "Deceased" tag and the time of death, already
   * formatted ("28 Sep 2026 14:05"), after the name
   * (docs/plans/phase2-mortuary-and-notifications.md §6).
   */
  deceased?: { diedAt: string };
  /**
   * An open pregnancy, in words: "Pregnant, EDD 6 Dec 2026, 24 weeks 3 days"
   * (docs/plans/phase2-maternity-mch.md §1.1 item 11). Absent for everyone else.
   */
  pregnancy?: string;
  /**
   * Isolation and precautions in place, in words: "Contact precautions"
   * (docs/plans/phase3-operations-modules.md §2.1). Passive outlined tags; never the danger
   * colour and never blocking.
   */
  precautions?: readonly string[];
}

const AGE_UNIT: Record<Age['unit'], string> = { years: 'y', months: 'mo', days: 'd' };

/**
 * `34 y`, `8 mo`, `6 d`, with ` (estimated)` when the date of birth was estimated
 * (registration plan §12, decision 12).
 */
export function formatShortAge(age: Age | null, estimated: boolean): string {
  if (age === null) return 'Age not recorded';
  const text = `${String(age.value)} ${AGE_UNIT[age.unit]}`;
  return estimated ? `${text} (estimated)` : text;
}

function allergyText(allergy: BannerAllergy): string {
  return allergy.reaction === undefined || allergy.reaction === ''
    ? allergy.substance
    : `${allergy.substance} (${allergy.reaction})`;
}

function Allergies({ allergies }: { allergies: BannerAllergies }) {
  if (allergies.state === 'list' && allergies.allergies.length > 0) {
    return (
      <span className={styles.allergies}>
        <span>Allergies:</span>
        <ul className={styles.allergyList}>
          {allergies.allergies.map((allergy) => (
            <li key={allergyText(allergy)} className={styles.allergyTag}>
              {allergyText(allergy)}
            </li>
          ))}
        </ul>
      </span>
    );
  }
  if (allergies.state === 'no_known_allergies') {
    return <span className={styles.strong}>No known allergies</span>;
  }
  return <span className={styles.outlinedTag}>Allergies not recorded</span>;
}

/**
 * The patient banner (DESIGN.md §6.2): solid ink, full width, 0 radius, at least 88px,
 * sticky under the top bar, on every patient-context screen. It shows name, sex, age,
 * identifiers, payer and allergies. Identifiers are in the mono face. A long allergy list
 * wraps and the banner grows rather than hiding any allergy.
 */
export function PatientBanner({
  name,
  sex,
  age,
  ageEstimated,
  patientNumber,
  crId,
  registryStatus,
  payer,
  visit,
  allergies,
  href,
  unidentified,
  alsoRecordedAs = [],
  deceased,
  pregnancy,
  precautions = [],
}: PatientBannerProps) {
  return (
    <section className={styles.banner} aria-label={`Patient: ${name}`}>
      <div className={styles.line}>
        {href === undefined ? (
          <span className={styles.name}>{name}</span>
        ) : (
          <Link href={href} className={`${styles.name} ${styles.nameLink}`}>
            {name}
          </Link>
        )}
        {deceased === undefined ? null : (
          <>
            <span className={styles.solidTag}>Deceased</span>
            <span className={styles.strong}>{`Died ${deceased.diedAt}`}</span>
          </>
        )}
        {unidentified === undefined ? null : (
          <>
            <span className={styles.outlinedTag}>Not identified</span>
            <span>
              <span className={styles.visuallyHidden}>Temporary label </span>
              <span className={styles.id}>{unidentified.temporaryLabel}</span>
            </span>
          </>
        )}
        {/* The one place a middle dot is used: the clinical shorthand "F · 34 y". */}
        <span className={styles.strong}>{`${sex} · ${formatShortAge(age, ageEstimated)}`}</span>
        <span>
          <span className={styles.visuallyHidden}>Patient number </span>
          <span className={styles.id}>{patientNumber}</span>
        </span>
        {crId === null ? (
          <span className={styles.meta}>{registryStatus ?? 'No CR ID'}</span>
        ) : (
          <span>
            CR<span className={styles.visuallyHidden}> ID</span>{' '}
            <span className={styles.id}>{crId}</span>
          </span>
        )}
      </div>
      <div className={styles.line}>
        {payer === undefined ? null : <span>{payer}</span>}
        <Allergies allergies={allergies} />
        {pregnancy === undefined ? null : <span>{pregnancy}</span>}
        {precautions.length === 0 ? null : (
          <ul className={styles.precautions} aria-label="Precautions">
            {precautions.map((words) => (
              <li key={words} className={styles.outlinedTag}>
                {words}
              </li>
            ))}
          </ul>
        )}
        {alsoRecordedAs.length === 0 ? null : (
          <span className={styles.meta}>
            {'Also recorded as '}
            {alsoRecordedAs.map((label, i) => (
              <span key={label}>
                {i === 0 ? '' : ', '}
                <span className={styles.id}>{label}</span>
              </span>
            ))}
          </span>
        )}
        {visit === undefined ? (
          <span className={styles.meta}>No open visit</span>
        ) : (
          <span>
            {`${visit.type} visit `}
            <span className={styles.id}>{visit.number}</span>
          </span>
        )}
      </div>
    </section>
  );
}
